// Presentational tracking-download panel. Renders the per-team manifest as
// bordered team containers with a status badge, and — for downloadable teams —
// each present artifact as a row with its own download button. Downloads are
// per-file, one per click: browsers reliably handle a single cross-origin
// download per user gesture but silently drop most of a programmatic multi-file
// burst, so there is no "download all" here (the durable multi-file fix is a
// server-side zip archive). All data (manifest, loading/error signal) arrives
// via props so every state is testable without a network.
import type {
  TeamTrackingSet,
  TrackingArtifact
} from '@frontend/core/lib/domains/sessions/tracking'
import { Badge, Button, Spinner } from '@frontend/ui'
import { Download } from 'lucide-react'
import type { TrackingFetchState } from './use-tracking-download'

// Artifact field → display label, in the order they should appear in a row.
const ARTIFACT_LABELS = [
  ['metadata', 'Metadata'],
  ['standardTracking', 'Standard tracking'],
  ['highRateBall', 'High-rate ball']
] as const

// File-extension → the short format hint shown beside each artifact.
const FORMAT_LABELS: Record<string, string> = {
  json: 'JSON',
  parquet: 'Parquet',
  csv: 'CSV',
  xml: 'XML'
}

function teamLabel(team: TeamTrackingSet) {
  return team.teamName ?? team.teamId
}

function artifactEntries(team: TeamTrackingSet) {
  return ARTIFACT_LABELS.flatMap(([key, label]) => {
    const artifact = team[key]

    return artifact != null ? [{ key, label, artifact }] : []
  })
}

function formatHint(object: string) {
  const ext = object.includes('.')
    ? object.split('.').pop()?.toLowerCase()
    : undefined
  if (!ext) return ''

  return FORMAT_LABELS[ext] ?? ext.toUpperCase()
}

// The user-facing status shown as a badge in each team's header.
function teamStatus(
  team: TeamTrackingSet,
  downloadable: boolean
): { label: string; variant: 'positive' | 'secondary' } {
  if (downloadable) return { label: 'Available', variant: 'positive' }
  if (team.status === 'withheld')
    return { label: 'Restricted', variant: 'secondary' }
  if (team.status === 'not_instrumented') {
    return { label: 'Not tracked', variant: 'secondary' }
  }

  return { label: 'Not yet available', variant: 'secondary' }
}

interface TrackingDownloadPanelProps {
  state: TrackingFetchState
  onDownload: (artifacts: TrackingArtifact[], teamName: string) => void
  onRetry: () => void
  onClose: () => void
}

export function TrackingDownloadPanel({
  state,
  onDownload,
  onRetry,
  onClose
}: TrackingDownloadPanelProps) {
  const manifest =
    state.status === 'loaded' && state.result.kind === 'entitled'
      ? state.result.manifest
      : null
  const teams = manifest?.teams ?? []

  const teamRows = teams.map((team) => {
    const entries = artifactEntries(team)

    return {
      team,
      entries,
      downloadable: team.status === 'delivered' && entries.length > 0
    }
  })
  const hasAnyDownloadable = teamRows.some((row) => row.downloadable)

  if (state.status === 'loading') {
    return (
      <div
        role='status'
        aria-live='polite'
        className='flex min-h-full items-center justify-center gap-2 py-8'
      >
        <Spinner />
        <span>Loading tracking data…</span>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className='flex min-h-full flex-col'>
        <p role='alert' className='flex-1'>
          Couldn&apos;t load tracking data. Try again.
        </p>
        <div className='flex justify-between'>
          <Button onClick={onRetry}>Retry</Button>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  if (state.result.kind === 'none-entitled') {
    return (
      <EmptyState
        message="You don't have access to tracking data for this session."
        onClose={onClose}
      />
    )
  }

  if (state.result.kind === 'never-exported') {
    return (
      <EmptyState
        message="Tracking data hasn't been exported for this session."
        onClose={onClose}
      />
    )
  }

  return (
    <div className='flex min-h-full flex-col'>
      <div className='flex-1 space-y-3'>
        {!hasAnyDownloadable && (
          <p className='text-muted-foreground text-sm'>
            No downloadable tracking files for this session.
          </p>
        )}

        {teamRows.map((row) => {
          const status = teamStatus(row.team, row.downloadable)

          return (
            <div
              key={row.team.teamId}
              className='space-y-2 rounded-md border p-3'
            >
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{teamLabel(row.team)}</span>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>
              {row.downloadable && (
                <ul className='space-y-1'>
                  {row.entries.map((entry) => {
                    const hint = formatHint(entry.artifact.object)

                    return (
                      <li
                        key={entry.key}
                        className='flex items-center justify-between gap-2'
                      >
                        <span className='flex items-center gap-2 text-sm'>
                          {entry.label}
                          {hint && (
                            <span className='text-muted-foreground text-xs'>
                              {hint}
                            </span>
                          )}
                        </span>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          aria-label={`Download ${entry.label}`}
                          onClick={() =>
                            onDownload([entry.artifact], teamLabel(row.team))
                          }
                        >
                          <Download />
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      <div className='mt-6 space-y-4'>
        <p className='text-muted-foreground text-sm'>
          Schema v{manifest?.schemaVersion}
        </p>

        <div className='flex justify-end'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

function EmptyState({
  message,
  onClose
}: {
  message: string
  onClose: () => void
}) {
  return (
    <div className='flex min-h-full flex-col'>
      <p className='flex-1'>{message}</p>
      <div className='flex justify-end'>
        <Button variant='outline' onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}
